<script lang="ts" setup>
import { onKeyStroke, useMediaQuery } from '@vueuse/core';
import { useFocusTrap } from '~kui/composables/useFocusTrap';

interface Props {
  mode?: 'fixed' | 'drawer';
  label: string;
  testId?: string;
  backdropTestId?: string;
  widthClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'fixed',
  testId: undefined,
  backdropTestId: undefined,
  widthClass: 'w-64',
});

const open = defineModel<boolean>('open', { default: false });

const { elementRef: drawerRef, activate, deactivate } = useFocusTrap({
  escapeDeactivates: false,
  allowOutsideClick: true,
});

// On desktop the drawer is rendered inline alongside content (no backdrop), so the focus trap
// would steal focus from sibling UI like modals. Only trap when it actually behaves as an overlay.
const isMobile = useMediaQuery('(max-width: 767px)');

let stopEscapeListener: (() => void) | null = null;

const applyTrapState = () => {
  if (props.mode !== 'drawer' || !isMobile.value) {
    deactivate();
    stopEscapeListener?.();
    stopEscapeListener = null;
    return;
  }
  if (open.value) {
    stopEscapeListener?.();
    stopEscapeListener = null;
    void nextTick(() => activate());
    stopEscapeListener = onKeyStroke('Escape', () => {
      open.value = false;
    });
  }
  else {
    deactivate();
    stopEscapeListener?.();
    stopEscapeListener = null;
  }
};

// Sync with state changes after mount.
watch(() => [props.mode, open.value, isMobile.value] as const, applyTrapState);
// `useFocusTrap` auto-activates on mount. Re-apply our intended trap state
// after it runs so the trap is torn down when the drawer isn't actually open
// (e.g. desktop, where the drawer is visible via CSS but `open` is false).
onMounted(applyTrapState);

// Suppress the transform transition until after the first post-hydration update flushes, so any
// SSR→client reconciliation (e.g. a desktop-rendered drawer snapping closed once the mobile media
// query resolves) lands without an entrance animation. User-driven toggles still animate.
const transitionsEnabled = ref(false);
onMounted(() => {
  void nextTick(() => {
    transitionsEnabled.value = true;
  });
});

const asideClasses = computed(() =>
  twMerge(
    cva('bg-white flex flex-col transition-transform', {
      variants: {
        mode: {
          fixed:
            'fixed inset-y-0 left-0 z-40 border-r border-border duration-200',
          drawer:
            'h-dvh duration-300 fixed top-0 left-0 z-50',
        },
        open: {
          true: 'translate-x-0',
          false: '-translate-x-full',
        },
        transitionsEnabled: {
          true: '',
          false: 'transition-none',
        },
      },
    })({ mode: props.mode, open: open.value, transitionsEnabled: transitionsEnabled.value }),
    props.widthClass,
  ),
);
</script>

<template>
  <Teleport v-if="mode === 'drawer'" to="body">
    <Transition
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-40 bg-black/40 md:hidden"
        aria-hidden="true"
        :data-testid="backdropTestId"
        @click="open = false"
      />
    </Transition>
  </Teleport>

  <aside
    ref="drawerRef"
    :class="asideClasses"
    :aria-label="label"
    :data-testid="testId"
  >
    <div v-if="$slots.header">
      <slot name="header" />
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto">
      <slot />
    </div>
    <div v-if="$slots.footer">
      <slot name="footer" />
    </div>
  </aside>
</template>
